import { Button } from "@mui/base";
import { Tooltip } from "@mui/material";
import { CiCircleInfo } from "react-icons/ci";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";

export const NavigationText = {
  previous: "Previous",
  next: "Next",
};

export const WelcomeText = {
  title: "Business Case Form",
  subTitle:
    "Welcome to the UDig business case form! This form is intended to help you make profitable technology investment decisions via a set of step-by-step instructions to create a business case for your project.",

  name: {
    label: "Full Name",
  },
  email: {
    label: "Business Email",
  },
  company: {
    label: "Company",
  },
  logoAltText: "UDig Logo",
};

export const InstructionsText = {
  title: "Instructions",
  subTitle: "Before you begin, we wanted to share a few notes with you",
  navigation: {
    header: "Navigation",
    body: (
      <>
        <ul className="list-disc ml-5">
          <li>
            <div className="flex flex-wrap whitespace-wrap">
              Use the
              <Button className="flex mx-2 text-UDig-orange items-center">
                <BsArrowLeft className="mr-2" />
                Previous
              </Button>
              and
              <Button className="text-UDig-orange mx-2 flex items-center">
                Next
                <BsArrowRight className="ml-2" />
              </Button>
              buttons to navigate between pages
            </div>
          </li>
          <li>
            After reaching the Summary page, use the sidebar to navigate to a
            specific page
          </li>
        </ul>
      </>
    ),
  },
  forms: {
    header: "Forms",
    body: (
      <>
        Complete fields as necessary and to the best of your ability. Empty
        fields will default to zero where applicable.
        <ul className="list-disc ml-5">
          <li>
            Fields marked with <span className="text-red-500 font-bold">*</span>{" "}
            are required
          </li>
          <li>
            <div className="flex flex-wrap whitespace-wrap">
              Hover over the
              <Tooltip
                title="This is an example of a tooltip"
                placement="right"
                arrow
              >
                <Button>
                  <CiCircleInfo className="h-5 w-5 text-UDig-gray m-1" />
                </Button>
              </Tooltip>
              icon for more information about a field
            </div>
          </li>
          <li>Enter currency values in USD for accurate results</li>
        </ul>
      </>
    ),
  },
  sessions: {
    header: "Sessions",
    body: "Loading a new instance of the Business Case Form starts an entirely new session. Copy and save the URL from your browser so you can return to this session and share it with others.",
  },
  contact: {
    header: "Need Help?",
    body: (
      <>
        If you experience any trouble or have questions while using the Business
        Case Form you can reach out to{" "}
        <a href="mailto:contact@udig.com" className="text-UDig-orange">
          contact@UDig.com
        </a>{" "}
        for further assitance.
      </>
    ),
  },
};

export const ObjectiveText = {
  title: "Objective",
  subTitle: "Input your company's objective",
  objective: {
    label: "Objective",
    tooltip:
      "E.g: Ensure everyone can do their best work by making trusted data easy to work by making trusted data easy to the organization.",
  },
};

export const CostsText = {
  title: "Costs",
  subTitle: "Enter the costs you expect to incur to deliver your project",
  card: {
    label: "Cost",
    detailsSection: "Cost Details",
    yearlySection: "Yearly Cost",
    monthlySection: "Monthly Costs",
    removeTooltip: "Remove this cost",
    addCard: {
      label: "New Cost",
      tooltip: "Add another cost",
    },
  },
  toggle: {
    ongoing: {
      label: "Ongoing Annual Cost",
      tooltip: "For costs that do not change from year to year",
    },
    oneTime: {
      label: "One-time or Variable Cost",
      tooltip: "For costs that are only incurred once or that change over time",
    },
  },
  category: {
    label: "Category",
    tooltip: "The name of this cost category",
    placeholder: "Enter a custom category",
    dropdown: [
      "Software Expenses",
      "Maintenance Expenses",
      "Hardware Expenses",
      "Contract Labor",
      "Set-up Costs",
      "Other",
      "Custom",
    ],
    cancel: {
      label: "Cancel",
      tooltip: "Stop entering a custom category",
    },
  },
  yearlyCost: {
    label: "Yearly Cost",
    tooltip: "The cost of this expense category each year (USD)",
  },
  description: {
    label: "Description",
    tooltip: "Explain this category and how you determined it's cost",
    placeholder: "",
  },
  monthlyCosts: {
    label: "Monthly Breakdown",
    tooltip: "The cost of this category broken down by month (USD)",
  },
  numberOfYears: {
    label: "Number of Years",
    tooltip:
      "The period which you will be paying for this cost (up to 5 years)",
  },
};

export const QuantitativeText = {
  title: "Quantitative Financial Benefits",
  subTitle:
    "Enter the financial benefits you expect to generate from the proposed project",
  card: {
    label: "Financial Benefit",
    detailsSection: "Benefit Details",
    yearlySection: "Yearly Benefit",
    monthlySection: "Monthly Benefits",
    removeTooltip: "Remove this financial benefit",
    addCard: {
      label: "New Benefit",
      tooltip: "Add another financial Benefit",
    },
  },
  toggle: {
    ongoing: {
      label: "Ongoing Annual Benefit",
      tooltip: "For benefits that do not change year to year",
    },
    oneTime: {
      label: "One-time or Variable Benefit",
      tooltip:
        "For benefits that are only incurred once or that change over time",
    },
  },
  category: {
    label: "Category",
    tooltip: "The name of this financial benefit category",
    placeholder: "Enter a custom category",
    dropdown: [
      "Increase in Revenue",
      "Reduction in Overhead",
      "Reduce Legal Compliance Risk",
      "Reduce Brand Risk",
      "Other",
      "Custom",
    ],
    cancel: {
      label: "Cancel",
      tooltip: "Stop entering a custom category",
    },
  },
  yearlyBenefit: {
    label: "Yearly Benefit",
    tooltip: "The amount you benefit from this category each year (USD)",
  },
  description: {
    label: "Description",
    tooltip: "Explain this category and how you determined it's benefit",
    placeholder: "",
  },
  monthlyBenefits: {
    label: "Monthly Breakdown",
    tooltip: "The benefits gained in this category broken down by month (USD)",
  },
  numberOfYears: {
    label: "Number of Years",
    tooltip: "The number of years you will earn this benefit (up to 5 years)",
  },
};

export const QualitativeText = {
  title: "Qualitative Benefits",
  subTitle:
    "You can use this section to list strategic benefits that are difficult to quantify",
  card: {
    label: "Benefit List",
    removeTooltip: "Remove this benefit list",
    addCard: {
      label: "New Benefit List",
      tooltip: "Add another category for benefits",
    },
  },
  category: {
    label: "Category",
    tooltip: "The name for the list of benefits below",
    placeholder: "Enter a custom category",
    cancel: {
      label: "Cancel",
      tooltip: "Stop entering a custom category",
    },
    dropdown: [
      "E.g. Operational",
      "E.g. Customer",
      "E.g. Employee",
      "E.g. Risk Management",
    ],
  },
  benefits: {
    label: "Benefits",
    tooltip:
      "The list of benefits (I.e. customer experience, associate engagement, compliance, etc.)",
    removeTooltip: "Remove this benefit",
    addBenefit: {
      label: "Add Benefit",
      tooltip: "Add another benefit to this category",
    },
  },
};

export const AssumptionsText = {
  title: "Assumptions",
  subTitle: "Input and explain your company's internal hurdle rate",
  internalHurdleRate: {
    label: "Internal Hurdle Rate",
    tooltip: (
      <>
        <div>
          This is your corporate cost of capital used for discounting cash flows
          - ask your finance team for guidance.
        </div>
        <div>Typically in the 8% - 16% range</div>
      </>
    ),
    definition:
      "The internal rate of return is the amount above the break-even point that an investment may earn.",
  },
  description: {
    label: "Description",
    tooltip: "Explain the factors of your internal hurdle rate",
    placeholder: "",
  },
};

export const ReviewText = {
  title: "Review",
  subTitle:
    "Double check the information below to make sure everything is correct",
  default: {
    objective: "Press Edit to enter an objective for this project",
    costs: "Press Edit to enter costs for this project",
    quantitative: "Press Edit to enter financial benefits for this project",
    qualitative: "Press Edit to enter qualitative benefits for this project",
    assumptions: "Assumptions",
  },
  editButton: "Edit",
  submitButton: "Submit",
};

export const SummaryText = {
  title: "Summary",
  subTitle:
    "A comprehensive analysis of the costs and benefits of your project",
  objective: "Objective",
  analysis: {
    label: "Analysis",
    projectCost: {
      label: "Project Cost",
      tooltip:
        "The total cost over the life of the project (not discounted using the hurdle rate)",
    },
    benefits: {
      label: "Benefits",
      tooltip:
        "The total benefits over the life of the project (not discounted using the hurdle rate)",
    },
    netProfit: {
      label: "Net Profit",
      tooltip:
        "The benefits minus project costs (not discounted using the hurdle rate)",
    },
    netPresentValue: {
      label: "Net Present Value",
      tooltip:
        "The difference between the present value of cash inflows and the present value of cash outflows over a period of time",
    },
    paybackPeriod: {
      label: "Payback Period",
      tooltip:
        "The number of months to achieve a cumulative positive cash flow from the investment",
      error: {
        label: "No Payback",
        message:
          "Payback period requires sufficient cost and benefit data to calculate. Ensure complete financial information to view the payback period",
      },
    },
    ROI: {
      label: "ROI",
      tooltip: "The ratio of net profit over the total cost of the investment",
      error: {
        label: "Invalid ROI",
        message:
          "ROI calculation requires complete cost and benefit data for accurate results. Ensure all financial information is provided to view the ROI value",
      },
    },
    IRR: {
      label: "IRR",
      tooltip:
        "The annual rate of growth an investment is expected to generate",
      error: {
        label: "Invalid IRR",
        message:
          "IRR calculation requires valid cash flows and time periods. Ensure accurate financial data to view the IRR value",
      },
    },
  },
  financials: {
    label: "Financials",
    totalCosts: "Total Costs",
    totalBenefits: "Total Benefits",
    netProfit: "Net Profit",
    discountedNetProfit: "Discounted Net Profit",
    year: "Year",
    total: "Total",
  },
  costsBenefitsGraph: "Costs vs Benefits by Year",
  cumulativeCashFlow: "Cumulative Cash Flow",
  benefits: "Benefits",
  printButton: "Print",
};
